/**
 * <s-back-in-stock> — Back-in-stock sign-up for a sold-out variant.
 *
 * Posts the email to STOQ's public signup endpoint (CORS-open, no API key; the
 * shop is identified by its myshopify domain). STOQ then emails the shopper
 * automatically when the variant is restocked. No app embed is required, which
 * is why this works on the Canvas theme. The element lives in the product-form
 * partial, so it is re-created with the right variant on every option change.
 *
 * Required child markup:
 *   input[data-notify-email]
 *   button[data-notify-submit]
 *   [data-notify-fields]   — Hidden after a successful sign-up.
 *   [data-notify-status]   — Status / error message (role="status").
 *
 * Element dataset:
 *   data-variant-id, data-product-id
 *   data-shop      — shop.permanent_domain
 *   data-endpoint  — STOQ signup endpoint
 *   data-country   — Visitor country (lowercase ISO), optional
 *   data-sending, data-success, data-invalid, data-error — Translated messages.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class SBackInStock extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const button = this.querySelector('[data-notify-submit]');
    const input = this.querySelector('[data-notify-email]');

    button?.addEventListener('click', () => this.#submit(), opts);
    input?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.#submit();
        }
      },
      opts,
    );
    input?.addEventListener('input', () => input.removeAttribute('aria-invalid'), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #status(message, tone) {
    const status = this.querySelector('[data-notify-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('text-sale', tone === 'error');
    status.classList.toggle('text-on-surface', tone !== 'error');
  }

  async #submit() {
    if (this.#busy) return;
    const input = this.querySelector('[data-notify-email]');
    const email = (input?.value || '').trim();

    if (!EMAIL_PATTERN.test(email)) {
      input?.setAttribute('aria-invalid', 'true');
      input?.focus();
      this.#status(this.dataset.invalid, 'error');
      return;
    }

    this.#busy = true;
    this.#status(this.dataset.sending, 'info');

    const customer = { email, accepts_marketing: false };
    if (this.dataset.country) customer.country = this.dataset.country;

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Shop-Domain': this.dataset.shop,
        },
        body: JSON.stringify({
          intent: {
            shopify_variant_id: Number(this.dataset.variantId),
            shopify_product_id: Number(this.dataset.productId),
            channel: 'email',
            quantity: 1,
            source: 'api',
          },
          customer,
        }),
      });

      // 201 = created. 200 means "processed but with errors", so it is not a success.
      if (response.status !== 201) {
        const detail = await response.text().catch(() => '');
        throw new Error(`STOQ responded ${response.status}: ${detail.slice(0, 200)}`);
      }

      const fields = this.querySelector('[data-notify-fields]');
      if (fields) fields.hidden = true;
      this.#status(this.dataset.success, 'info');
    } catch (error) {
      console.warn('[back-in-stock]', error);
      this.#status(this.dataset.error, 'error');
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('s-back-in-stock')) {
  customElements.define('s-back-in-stock', SBackInStock);
}
